import React, { useState, useRef, useEffect, useMemo, useReducer } from 'react';
import styles from './ViewGoals.module.scss';
import Icon from '../../../common/Icon/Icon';
import { useAppSelector } from '../../../../redux/hooks';
import useClickOutside from '../../../CustomHooks/useClickOutside';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import DropdownMenu from '../../../common/DropdownMenu/DropdownMenu';
import AddActionModal from '../Goals/AddActionModal/AddActionModal';
import Modal from '../../../common/Modal/Modal';
import { disableEnableScroll } from '../../../../utils/disableEnableScroll';
import AddMetricsModal from '../Goals/AddMetricModal/AddMetricModal';
import GoalHeader from '../GoalsHeader/GoalsHeader';
import GoalsDetailsPage from '../GoalsDetailsPage/GoalsDetailsPage';
import useClickOutsideMap from '../../../CustomHooks/useClickOutSideMap';
import DeleteConfirmationPopUp from '../../../common/DeleteConfirmationPopUp/DeleteConfirmationPopUp';
import { useTranslation } from 'react-i18next';
import {
  ACTIONS,
  countryCodeMappings,
  GOAL_ACTION_STATUS,
  PERFORMANCE_AREA,
  PERFORMANCE_METRICS,
} from '../../../../constants/dataConstants';
import { goalModalReducer, useProfileParams } from '../../../../utils/common';
import { getGoalActionStatusLabel, getMetricLabel } from '../../../../utils/goalUtil';
import { formatDate } from '../../../../utils/dateFormatter';
import { useDeleteGoalActionMutation } from '../../../../redux/services/goalsAPI';
import { StatusDropdown } from '../../../common/StatusDropdown/StatusDropdown';
import { useUpdateGoalActionStatusMutation } from '../../../../redux/services/goalActionsAPI';
import { useDeleteGoalMetricMutation } from '../../../../redux/services/goalMetricAPI';
import isEmpty from 'lodash/isEmpty';
import { formatDateShort } from '../../../../utils/common';

type ViewGoalsProps = {
  goalData: GoalData | HistoryItem;
  setShowDetailsPage: React.Dispatch<React.SetStateAction<boolean>>;
  showDetailsPage: boolean;
  onGoalSave?: (goalData: GoalData) => void;
  selectedGoalId: number | null;
  setSelectedGoalId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedHistoryData: HistoryItem | null;
};

const ViewGoals = ({
  goalData,
  setShowDetailsPage,
  showDetailsPage,
  onGoalSave,
  selectedGoalId,
  setSelectedGoalId,
  selectedHistoryData,
}: ViewGoalsProps) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const { affiliateCode, aboNumber } = useProfileParams();

  // const [grids, setGrids] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState<{
    [key: string]: boolean;
  }>({});
  const [showMetricsDropdown, setShowMetricsDropdown] = useState<{
    [key: string]: boolean;
  }>({});

  const [selectedItem, setSelectedItem] = useState<string>('On Track');
  const [selectedMetricsItem, setSelectedMetricsItem] = useState<{
    [key: string]: string;
  }>({});
  const [selectedStatusItem, setSelectedStatusItem] = useState<{
    [key: string]: string;
  }>({});
  const [selectedActionStatusItem, setSelectedActionStatusItem] = useState<{
    [key: string]: string;
  }>({});
  const [selectedMetricsMap, setSelectedMetricsMap] = useState<{
    [gridId: string]: MetricRow[];
  }>({});

  const [modalOpen, setModalOpen] = useState(false);
  const [showViewAction, setShowViewAction] = useState(false);
  const metricsItems = [
    { label: t('metrics_menu.edit'), value: ACTIONS.EDIT },
    { label: t('metrics_menu.delete'), value: ACTIONS.DELETE },
    // AMW-3385
    // t("metrics_menu.add_edit_metrics"),
  ];
  const [selectedActionId, setSelectedActionId] = useState<string | number>('');
  const [selectedMetricId, setSelectedMetricId] = useState<string | number | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const metricsDropdownRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const statusDropdownRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [metricsModalOpen, setMetricsModalOpen] = useState(false);

  useClickOutside(dropdownRef, () => setShowDropdown(false));
  const metricsModalRef = useRef<HTMLDivElement>(null);
  useClickOutsideMap(statusDropdownRef, setShowStatusDropdown, metricsModalRef);
  useClickOutsideMap(metricsDropdownRef, setShowMetricsDropdown, metricsModalRef);

  const [goalActions, setGoalActions] = useState<ActionDataResp[]>([]);

  useEffect(() => {
    if (!isEmpty(goalData?.goalActions)) {
      setGoalActions(goalData?.goalActions);
    }
  }, [goalData]);

  const selectedActionItem = useMemo(() => {
    return goalActions?.find((item) => item.aboGoalActionId === selectedActionId);
  }, [goalActions, selectedActionId]);

  const handleClickAddAction = () => {
    setEditActionID(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };
  const handleMetricsModalClose = () => setMetricsModalOpen(false);
  const openMetricsModal = (gridId: string | number) => {
    const selectedAction = goalActions?.find((item) => item.aboGoalActionId === gridId);
    if (selectedAction !== undefined && selectedAction?.goalMetrics?.length > 0) {
      setSelectedMetricsMap((prev: any) => ({
        ...prev,
        [gridId]: selectedMetricsMap[gridId] || selectedAction?.goalMetrics,
      }));
    }

    setSelectedActionId(gridId);
    setMetricsModalOpen(true);
  };

  useEffect(() => {
    disableEnableScroll(modalOpen);
    disableEnableScroll(metricsModalOpen);
  }, [modalOpen, metricsModalOpen]);

  const hasLeader = useMemo(() => {
    return goalActions?.some((item: any) => item.assignToType === 2);
  }, [goalActions]);

  const isHistoryGoalData = !isEmpty(selectedHistoryData);
  const isSelected =
    isHistoryGoalData || (showDetailsPage && selectedGoalId === goalData?.aboGoalId);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editActionID, setEditActionID] = useState<string | number | null>(null);
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);

  const handleDeleteModalToggle = () => {
    setDeleteModalOpen(!deleteModalOpen);
  };
  const [operation, dispatch] = useReducer(goalModalReducer, {
    type: '',
    payload: { gridId: '' },
  });

  useEffect(() => {
    if (!operation?.type) return;

    switch (operation.type) {
      case ACTIONS.ADD_ACTIONS:
        handleClickAddAction();
        break;
      case ACTIONS.ADD_EDIT_METRICS:
        openMetricsModal(operation.payload.gridId || '');
        break;
      case ACTIONS.EDIT:
        handleClickAddAction();
        setSelectedActionId(operation.payload.gridId || '');
        setEditActionID(operation.payload.gridId);
        break;
      case ACTIONS.DELETE:
        setSelectedActionId(operation.payload.gridId || '');
        setDeleteModalOpen(true);
        break;
      case ACTIONS.DELETE_METRICS:
        const metricId = operation.payload.gridId;
        deleteActionMetric(metricId);
        break;
      default:
        break;
    }
  }, [operation]);

  const handleOperation = (item: any, gridId: string | number | null) => {
    dispatch({
      type: item,
      payload: { gridId },
    });
  };

  const [
    updateGoalActionStatus,
    { isLoading: isLoadingUpdateGoalActionstatus, isError: isErrorUpdateGoalActionStatus },
  ] = useUpdateGoalActionStatusMutation();

  const [deleteAction, { isLoading: isLoadingDelete, isError: isErrorInDelete }] =
    useDeleteGoalActionMutation();

  const [deleteMetric, { isLoading: isLoadingDeleteMetric, isError: isErrorInDeleteMetric }] =
    useDeleteGoalMetricMutation();

  const confirmDelete = async () => {
    if (!selectedActionId) return;

    try {
      await deleteAction({ actionId: Number(selectedActionId) }).unwrap();
      setGoalActions((prev) =>
        prev.filter((action) => action.aboGoalActionId !== selectedActionId)
      );
    } catch (error) {
      console.error('Failed to delete goal action', error);
    }
    setSelectedActionId('');
    setDeleteModalOpen(false);
  };

  const deleteActionMetric = async (metricId: number | string | null) => {
    if (!metricId) return;
    try {
      await deleteMetric({ metricId: Number(metricId) });

      setSelectedMetricsMap((prevMetricsMap) => {
        const updatedMetricsMap = { ...prevMetricsMap };

        Object.keys(updatedMetricsMap).forEach((actionId) => {
          const metrics = updatedMetricsMap[actionId];
          updatedMetricsMap[actionId] = metrics.filter(
            (metric) => metric.aboGoalActionMetricId !== metricId
          );
        });

        return updatedMetricsMap;
      });
    } catch (error) {
      console.error('Failed to delete action metric', error);
    }
  };

  return (
    <div
      className={`view_goals_wrapper ${styles.view_goals_wrapper} ${showDetailsPage ? styles.isDetails : ''}`}
    >
      <svg width="0" height="0">
        <defs>
          <linearGradient id="customGradient" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C3EB82" />
            <stop offset="51.04%" stopColor="#24C072" />
            <stop offset="100%" stopColor="#198950" />
          </linearGradient>
        </defs>
      </svg>
      {isSelected && (
        <div className={`${styles.details_page_wrapper} ${showDetailsPage ? styles.show : ''}`}>
          <GoalHeader
            goalData={goalData}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            isDetailsPage={showDetailsPage}
            hasLeader={hasLeader}
            onGoalSave={onGoalSave}
            selectedStatusItem={selectedStatusItem}
            setSelectedStatusItem={setSelectedStatusItem}
            isHistoryGoalData={isHistoryGoalData}
          />
          <GoalsDetailsPage
            goalID={goalData?.aboGoalId}
            goalActions={goalActions}
            setGoalActions={setGoalActions}
            selectedMetricsMap={selectedMetricsMap}
            setSelectedMetricsMap={setSelectedMetricsMap}
            handleOperation={(item, gridId) => handleOperation(item, gridId)}
            selectedActionStatusItem={selectedActionStatusItem}
            setSelectedActionStatusItem={setSelectedActionStatusItem}
            isHistoryGoalData={isHistoryGoalData}
          />
        </div>
      )}

      {!showDetailsPage && (
        <>
          <GoalHeader
            goalData={goalData}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            hasLeader={hasLeader}
            selectedStatusItem={selectedStatusItem}
            setSelectedStatusItem={setSelectedStatusItem}
          />

          <div className={styles.actions_wrapper}>
            {/* card grid */}
            <div className={styles.cards_container}>
              {goalActions?.length > 0 &&
                goalActions.slice(0, 9).map((action) => (
                  <div
                    key={action?.aboGoalActionId}
                    className={`action_card_wrapper ${styles.action_card_wrapper} ${
                      action?.assignToType === 2 ? styles.topBorder : ''
                    }`}
                  >
                    {/* left side */}
                    <div className={styles.left_wrapper}>
                      <div className={styles.action_summary}>
                        <span className={styles.action_title}>{action?.title}</span>
                        <div
                          ref={(el) => (statusDropdownRef.current[action?.aboGoalActionId] = el)}
                          className={`status_date_wrapper ${styles.status_date_wrapper}`}
                        >
                          {
                            <StatusDropdown
                              options={GOAL_ACTION_STATUS}
                              selectedItem={
                                selectedActionStatusItem[action?.aboGoalActionId] ||
                                getGoalActionStatusLabel(action?.status) ||
                                'On Track'
                              }
                              setSelectedItem={(item, itemCode) => {
                                updateGoalActionStatus({
                                  affiliateCode,
                                  aboNumber,
                                  goalActionId: action?.aboGoalActionId,
                                  statusId: itemCode,
                                }).then((resp: any) => {
                                  const updateActionStatusData = resp?.data?.body;
                                  if (!isEmpty(updateActionStatusData)) {
                                    setSelectedActionStatusItem((prev) => ({
                                      ...prev,
                                      [action?.aboGoalActionId]: item,
                                    }));
                                    setShowStatusDropdown((prev) => ({
                                      ...prev,
                                      [action?.aboGoalActionId]: false,
                                    }));
                                  }
                                });
                              }}
                            />
                          }
                          {action?.targetDate && (
                            <div className={`target_date_wrapper ${styles.target_date_wrapper}`}>
                              <Icon
                                name="calendar_down"
                                size={14}
                                className={styles.calendar_icon}
                                color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
                              />
                              <span className={styles.date_value}>
                                {formatDateShort(action?.targetDate || '')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className={styles.add_matrics_wrapper}>
                        {
                          (selectedMetricsMap[action?.aboGoalActionId] || action?.goalMetrics)
                            ?.length > 0 &&
                            (
                              selectedMetricsMap[action?.aboGoalActionId] || action?.goalMetrics
                            )?.map((m, idx) => {
                              return (
                                <div
                                  key={idx}
                                  className={`progress_bar_wrapper ${styles.progress_bar_wrapper}`}
                                >
                                  <div className={styles.progress_bar}>
                                    <CircularProgressbarWithChildren
                                      value={m?.status === 1 ? 0 : 100}
                                      strokeWidth={10}
                                      className={styles['circular_progress_gradient']}
                                      styles={buildStyles({
                                        trailColor:
                                          currentTheme === 'light' ? '#D9D9D9' : '#282828',
                                      })}
                                    >
                                      {m?.status === 2 && (
                                        <Icon
                                          name="check_icon"
                                          size={14}
                                          className={styles.check_icon}
                                          color={currentTheme === 'light' ? '#107F47' : '#B3D67B'}
                                        />
                                      )}
                                    </CircularProgressbarWithChildren>
                                  </div>
                                  <span className={styles.progress_bar_text}>
                                    {getMetricLabel(m.kpiCode)}
                                  </span>
                                </div>
                              );
                            })
                          // Commented for AMW-3385
                          //  : (
                          //   <div
                          //     className={`matrics_inner_wrapper ${styles.matrics_inner_wrapper}`}
                          //     onClick={(e) => {
                          //       e.stopPropagation();
                          //       openMetricsModal(action?.aboGoalActionId);
                          //     }}
                          //   >
                          //     <Icon
                          //       name="plus_icon"
                          //       size={14}
                          //       className={styles.plus_icon}
                          //       color={
                          //         currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"
                          //       }
                          //     />
                          //     <span
                          //       className={`add_metrics_text ${styles.add_metrics_text}`}
                          //     >
                          //       {t("add_metrics")}
                          //     </span>
                          //   </div>
                          // )
                        }
                      </div>
                    </div>
                    <div className={styles.right_wrapper}>
                      <div
                        ref={(el) => (metricsDropdownRef.current[action?.aboGoalActionId] = el)}
                        className={`${styles.inner_wrapper} ${
                          showMetricsDropdown[action?.aboGoalActionId] ? styles.active : ''
                        }`}
                      >
                        <Icon
                          name="three_dot_icon"
                          size={12}
                          className={styles.three_dot_icon}
                          onClick={() =>
                            setShowMetricsDropdown((prev) => ({
                              ...prev,
                              [action?.aboGoalActionId]: !prev[action?.aboGoalActionId],
                            }))
                          }
                          color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
                        />
                        {showMetricsDropdown[action?.aboGoalActionId] && (
                          <DropdownMenu
                            items={metricsItems}
                            selectedItem={selectedMetricsItem[action?.aboGoalActionId]}
                            onSelect={(item: any) => {
                              setSelectedMetricsItem((prev) => ({
                                ...prev,
                                [action?.aboGoalActionId]: item,
                              }));
                              setShowMetricsDropdown((prev) => ({
                                ...prev,
                                [action?.aboGoalActionId]: false,
                              }));
                              handleOperation(item, action?.aboGoalActionId);
                            }}
                            isVisible={showMetricsDropdown[action?.aboGoalActionId]}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              {goalActions.length < 9 && (
                <div className={`add_wrapper ${styles.add_wrapper}`}>
                  <div className={styles.add_inner} onClick={handleClickAddAction}>
                    <Icon
                      name="plus_icon"
                      size={14}
                      className={styles.plus_icon}
                      color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
                    />
                    <span className={`add_action_text ${styles.add_action_text}`}>
                      {t('add_action')}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.details_page}>
              <div className={styles.circle_icon}>
                <Icon
                  name="chevron_right_icon"
                  size={16}
                  className={styles.chevron_icon}
                  color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
                  onClick={() => {
                    setShowDetailsPage((prev) => !prev);
                    setSelectedGoalId(goalData?.aboGoalId);
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <Modal showDrawer={modalOpen} onClose={handleModalClose}>
        <AddActionModal
          onClose={handleModalClose}
          editData={editActionID ? selectedActionItem : undefined}
          onSave={(newData) => {
            if (editActionID) {
              setGoalActions((prev) =>
                prev.map((item) => (item.aboGoalActionId === editActionID ? newData : item))
              );
            } else {
              setGoalActions((prev) => [...prev, newData]);
              setShowViewAction(true);
            }
            setEditActionID(null);
            setModalOpen(false);
          }}
          selectedGoalId={goalData?.aboGoalId}
        />
      </Modal>
      <Modal
        showDrawer={metricsModalOpen}
        onClose={handleMetricsModalClose}
        customWidthClass={styles.modal_width}
      >
        <div ref={metricsModalRef}>
          <AddMetricsModal
            handleOperation={(item, gridId) => handleOperation(item, gridId)}
            performanceAreas={PERFORMANCE_AREA}
            performanceMetric={PERFORMANCE_METRICS}
            onChange={(metrics: MetricRow[]) => {
              setSelectedMetricsMap((prev) => ({
                ...prev,
                [selectedActionId as string]: metrics,
              }));
            }}
            onClose={handleMetricsModalClose}
            selectedMetricsMap={selectedMetricsMap}
            selectedActionId={selectedActionId}
          />
        </div>
      </Modal>
      <Modal showDrawer={deleteModalOpen} onClose={handleDeleteModalToggle}>
        <DeleteConfirmationPopUp
          id={'actionDeleteConfirmation'}
          titleLabel={t('delete_confirmation_headerText')}
          msgText={t('delete_confirmation_msg')}
          buttonLabelOne={t('no_cancel')}
          buttonLabelTwo={t('yes_delete')}
          onCancel={handleDeleteModalToggle}
          onDelete={confirmDelete}
        />
      </Modal>
    </div>
  );
};

export default ViewGoals;
